import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInjection } from "@presentation/components/dependencyInjection";
import { SaveLicenseService } from "@application/useCases/license/SaveLicenseService";
import { TYPES } from "@utils/inversifyJS/types";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { invalidateLicensedSoftwareCache } from "@presentation/components/job/components/useLicensedSoftware";
import type { SupportedSoftware } from "@domain/repositories/ISoftwareVersionsRepository";

/**
 * How often the About page re-reads the license status while an activation is running.
 */
const ACTIVATION_POLL_INTERVAL_MS = 5000;

/**
 * How long we keep reporting "activating" before telling the user it most likely failed.
 *
 * The cluster never reports activation failures back (a rejected key simply leaves the license
 * secrets unwritten), so a timeout is the only signal we can give.
 */
const ACTIVATION_TIMEOUT_MS = 5 * 60 * 1000;

type ActivationPhase = "idle" | "activating" | "active" | "timedOut" | "failed";

const ACTIVATION_MESSAGES: Record<Exclude<ActivationPhase, "idle">, { text: string; color: string }> = {
  activating: {
    text: "Key submitted. The cluster is now running an activation job for it; this usually takes a minute or two. Keep this page open to follow the progress.",
    color: "#b45309",
  },
  active: {
    text: "Activated. The license is now present in the cluster and the engine can be selected in Job Setup.",
    color: "green",
  },
  timedOut: {
    text: "Still not activated after 5 minutes. The activation job reports no result when a key is rejected, so the key was most likely invalid or already in use. Check the key and try again, or contact support.",
    color: "#b00020",
  },
  failed: {
    text: "The key could not be submitted. Check your connection and try again.",
    color: "#b00020",
  },
};

/**
 * Tracks an engine from "key submitted" to "license visible in the cluster".
 *
 * Activation happens asynchronously in a Kubernetes job and nothing is reported back on failure,
 * so this polls the license status and falls back to a timeout.
 */
const useActivationTracker = (saveLicenseService: SaveLicenseService, software: SupportedSoftware) => {
  const [phase, setPhase] = useState<ActivationPhase>("idle");
  const [startedAt, setStartedAt] = useState<number | null>(null);

  useEffect(() => {
    if (phase !== "activating" || startedAt === null) return;

    let isCancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      try {
        const status = await saveLicenseService.getStatus();
        if (isCancelled) return;
        const licensed = software === "SpectroMine" ? status.spectroMineLicensed : status.spectronautLicensed;
        if (licensed && status.known) {
          // Let Job Setup pick the new engine up instead of serving its cached (empty) list.
          invalidateLicensedSoftwareCache();
          setPhase("active");
          return;
        }
      } catch (error) {
        console.error("Could not read license status while activating", error);
      }
      if (isCancelled) return;
      if (Date.now() - startedAt >= ACTIVATION_TIMEOUT_MS) {
        setPhase("timedOut");
        return;
      }
      timer = setTimeout(poll, ACTIVATION_POLL_INTERVAL_MS);
    };

    timer = setTimeout(poll, ACTIVATION_POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [phase, startedAt, saveLicenseService, software]);

  const start = () => {
    setStartedAt(Date.now());
    setPhase("activating");
  };

  return { phase, start, fail: () => setPhase("failed") };
};

const ActivationMessage: React.FC<{ phase: ActivationPhase }> = ({ phase }) => {
  if (phase === "idle") return null;
  const { text, color } = ACTIVATION_MESSAGES[phase];
  return (
    <div role="status" style={{ color, marginTop: 8, fontSize: "0.9em", maxWidth: 520 }}>
      {text}
    </div>
  );
};

/**
 * The about page with information about:
 * 1.The software version and build number;
 * 2.The list of third party libraries used;
 * 3.Allows user to paste and save a license key.
 * @returns Functional component
 */
const AboutPage: React.FC = () => {
  const [licenseKey, setLicenseKey] = useState("");
  const [spectroMineLicenseKey, setSpectroMineLicenseKey] = useState("");
  const [releaseNotes, setReleaseNotes] = useState("Loading release notes...");
  const [releaseNotesError, setReleaseNotesError] = useState("");
  const { t: loc } = useTranslation();

  const saveLicenseService = useInjection<SaveLicenseService>(TYPES.SaveLicenseService);
  const spectronautActivation = useActivationTracker(saveLicenseService, "Spectronaut");
  const spectroMineActivation = useActivationTracker(saveLicenseService, "SpectroMine");

  useEffect(() => {
    const fetchReleaseNotes = async () => {
      try {
        const response = await fetch("release-notes.md", { cache: "no-store" });
        if (!response.ok) {
          throw new Error(`Failed to load release notes (${response.status})`);
        }
        const text = await response.text();
        const normalizedReleaseNotes = text.trim();
        setReleaseNotes(normalizedReleaseNotes || "No release notes available yet.");
        setReleaseNotesError("");
      } catch (error) {
        setReleaseNotes("No release notes available yet.");
        setReleaseNotesError(error instanceof Error ? error.message : String(error));
      }
    };

    fetchReleaseNotes();
  }, []);

  const handleSaveLicenseKey = async () => {
    try {
      await saveLicenseService.execute(licenseKey, "Spectronaut");
      // Activation is asynchronous, but drop the cached status so Job Setup re-reads it.
      invalidateLicensedSoftwareCache();
      setLicenseKey("");
      spectronautActivation.start();
    } catch (error) {
      console.error(loc("errorMessage", String(error)));
      spectronautActivation.fail();
    }
  };

  const handleSaveSpectroMineLicenseKey = async () => {
    try {
      await saveLicenseService.execute(spectroMineLicenseKey, "SpectroMine");
      invalidateLicensedSoftwareCache();
      setSpectroMineLicenseKey("");
      spectroMineActivation.start();
    } catch (error) {
      console.error(loc("errorMessage", String(error)));
      spectroMineActivation.fail();
    }
  };

  return (
    <div>
      <div>
        <h3>OmicsFlow v{runtimeConfig.OMICSFLOW_GENERAL_VERSION}</h3>
        <h3>Release notes</h3>
        {releaseNotesError && (
          <div role="alert" style={{ color: "#b00020", marginBottom: 8 }}>
            {releaseNotesError}
          </div>
        )}
        <pre
          style={{
            maxHeight: 320,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            border: "1px solid #d7dee8",
            borderRadius: 8,
            padding: 12,
            backgroundColor: "#f8fafc",
          }}
        >
          {releaseNotes}
        </pre>
        <div style={{ marginTop: 20 }}>
          <div style={{ marginTop: 8, fontSize: "0.9em", color: "#64748b", maxWidth: 520 }}>
            Saving a key does not activate it directly: the key is handed to the cluster, which runs an activation job for the matching search engine and stores the resulting license. The engine only
            becomes selectable in Job Setup once that job has succeeded.
          </div>
          <div style={{ marginTop: 10 }}>
            <input type="text" placeholder="Paste your Spectronaut license key" value={licenseKey} onChange={(e) => setLicenseKey(e.target.value)} style={{ width: 300, marginRight: 10 }} />
            <button onClick={handleSaveLicenseKey} disabled={!licenseKey.trim()}>
              Save
            </button>
          </div>
          <ActivationMessage phase={spectronautActivation.phase} />

          <div style={{ marginTop: 10 }}>
            <input
              type="text"
              placeholder="Paste your SpectroMine license key"
              value={spectroMineLicenseKey}
              onChange={(e) => setSpectroMineLicenseKey(e.target.value)}
              style={{ width: 300, marginRight: 10 }}
            />
            <button onClick={handleSaveSpectroMineLicenseKey} disabled={!spectroMineLicenseKey.trim()}>
              Save
            </button>
          </div>
          <ActivationMessage phase={spectroMineActivation.phase} />
        </div>
        <h3>Third party libraries</h3>
        <a href="https://biognosys.com/content/uploads/2025/12/ThirdPartyDependences_OmicsFlow.pdf">https://biognosys.com/content/uploads/2025/12/ThirdPartyDependences_OmicsFlow.pdf</a>
      </div>
    </div>
  );
};

export default AboutPage;
